import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const SERVICES = [
  {
    id: "aircraft-for-sale",
    overline: "Acquisition & Brokerage",
    title: "Aircraft For Sale",
    copy: "On and off-market aircraft sourcing, acquisition advisory and discreet disposal. Tell us your mission profile and we will present aircraft that fit.",
    image:
      "https://images.unsplash.com/photo-1684838200815-36eef38f353c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxleGVjdXRpdmUlMjBwcml2YXRlJTIwamV0JTIwZGFya3xlbnwwfHx8fDE3OTE4OTAzMDl8MA&ixlib=rb-4.1.0&q=85",
    alt: "Private jet on the tarmac",
    span: "lg:col-span-7",
    testid: "service-aircraft-for-sale",
    enquire: "/enquire/aircraft",
    browse: { label: "View aircraft", to: "/aircraft-for-sale" },
  },
  {
    id: "aircraft-charter",
    overline: "On-Demand Charter",
    title: "Aircraft Charter",
    copy: "Point-to-point private charter across the UK, Europe and beyond. One enquiry, one dedicated broker, wheels up on your schedule.",
    image:
      "https://images.unsplash.com/photo-1661954864180-e61dea14208a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxwcml2YXRlJTIwamV0JTIwaW50ZXJpb3IlMjBsdXh1cnl8ZW58MHx8fHwxNzkxODkwMzA5fDA&ixlib=rb-4.1.0&q=85",
    alt: "Luxury private jet interior",
    span: "lg:col-span-5",
    testid: "service-aircraft-charter",
    enquire: "/enquire/charter",
    browse: { label: "Empty legs", to: "/empty-legs" },
  },
  {
    id: "yachts-for-sale",
    overline: "Brokerage",
    title: "Yachts For Sale",
    copy: "Motor yachts and superyachts sourced through our brokerage network, with the same discretion we bring to the air.",
    image:
      "https://images.unsplash.com/photo-1617905876311-5c4cad20de42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB5YWNodCUyMGRhcmslMjB3YXRlcnxlbnwwfHx8fDE3OTE4OTAzMDl8MA&ixlib=rb-4.1.0&q=85",
    alt: "Luxury yacht on dark water",
    span: "lg:col-span-5",
    testid: "service-yachts-for-sale",
    enquire: "/enquire/yacht",
    browse: { label: "View yachts", to: "/yachts" },
  },
  {
    id: "bullion",
    overline: "Tangible Assets",
    title: "Bullion & Commodities",
    copy: "Physical gold, silver and selected commodities for clients who want their wealth held in something real.",
    image:
      "https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYnVsbGlvbiUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc5MTg5MDMwOXww&ixlib=rb-4.1.0&q=85",
    alt: "Gold bullion texture against a dark background",
    span: "lg:col-span-7",
    testid: "service-bullion",
    enquire: "/enquire/bullion",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 lg:py-32 bg-obsidian"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">
            The Club
          </p>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight max-w-2xl">
            Four desks. One standard of discretion.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {SERVICES.map((service, i) => (
            <motion.article
              key={service.id}
              id={service.id}
              data-testid={service.testid}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.12 }}
              className={`group relative overflow-hidden rounded-sm border border-white/5 bg-surface min-h-[380px] flex flex-col justify-end scroll-mt-24 transition-transform duration-500 hover:-translate-y-1 hover:border-gold/40 ${service.span}`}
            >
              <img
                src={service.image}
                alt={service.alt}
                className="absolute inset-0 w-full h-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="relative z-10 p-8 lg:p-10">
                <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-3">
                  {service.overline}
                </p>
                <h3 className="font-serif font-light text-2xl lg:text-3xl text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-sm text-white/60 font-light leading-relaxed max-w-md mb-6">
                  {service.copy}
                </p>
                <div className="flex items-center gap-6">
                  <Link
                    to={service.enquire}
                    data-testid={`${service.testid}-enquire-link`}
                    className="inline-flex items-center gap-2 bg-gold text-black text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-sm transition-colors duration-300 hover:bg-gold-light"
                  >
                    Enquire
                    <ArrowUpRight size={14} />
                  </Link>
                  {service.browse && (
                    <Link
                      to={service.browse.to}
                      data-testid={`${service.testid}-browse-link`}
                      className="text-[11px] uppercase tracking-[0.2em] text-white/70 transition-colors duration-300 hover:text-gold"
                    >
                      {service.browse.label}
                    </Link>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
